import { forwardRef, type ButtonHTMLAttributes } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const VARIANT: Record<Variant, string> = {
  primary:
    'bg-brand text-brand-contrast shadow-[inset_0_1px_0_rgb(255_255_255/0.12)] hover:opacity-90 focus-visible:ring-2 focus-visible:ring-brand/40 disabled:opacity-50',
  secondary:
    'bg-bg text-fg border border-border hover:bg-bg-subtle focus-visible:ring-2 focus-visible:ring-brand/30 disabled:opacity-50',
  ghost:
    'bg-transparent text-fg hover:bg-bg-subtle focus-visible:ring-2 focus-visible:ring-brand/30 disabled:opacity-50',
  danger:
    'bg-danger text-fg-inverse hover:opacity-90 focus-visible:ring-2 focus-visible:ring-danger/40 disabled:opacity-50',
  accent:
    'bg-accent text-accent-contrast shadow-[inset_0_1px_0_rgb(255_255_255/0.12)] hover:opacity-90 focus-visible:ring-2 focus-visible:ring-accent/40 disabled:opacity-50',
};

const SIZE: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm rounded-md',
  md: 'h-10 px-4 text-sm rounded-md',
  lg: 'h-11 px-5 text-base rounded-md',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  const cls = [
    'inline-flex items-center justify-center gap-2 font-medium outline-none transition select-none active:scale-[0.98] motion-reduce:active:scale-100',
    VARIANT[variant],
    SIZE[size],
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      ref={ref}
      type={type}
      className={cls}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {children}
    </button>
  );
});
